/**
 * Host Hunt Page
 *
 * Host view into a singular hunt. Shows them all of the details they need
 * to run a hunt, including hunt time, teams, host scripts, etc.
 */
import React from 'react'
import Modal from 'react-bootstrap/Modal'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { navigate } from '../../utils/navigate'
import { Tabs, Tab, Accordion } from 'react-bootstrap'
import HostContext from '../../utils/host-context'
import HostLoginWrapper from '../../components/host/login-wrapper'
import API from '../../utils/api'
import { getTeamTimeDeduction } from '../../utils/team-timer'
import { filterPhotosForTeam } from '../../utils/host-team-photos'
import { getMediaUrl } from '../../utils/helpers'

import HostChecklistQuestion from '../../components/host/checklist-question'
import BonusPointsList from '../../components/host/bonus-points-list'
import TeamScore from '../../components/host/scoring'

import HostPhotoChecklistQuestion from '../../components/host/photo-checklist-question'
import SvgCirclePlus from '../../components/svg/plus-circle'
import SvgQRCode from '../../components/svg/qr-code'
import TeamProgressModal from '../../components/host/team-progress-modal'
import QRCodeModal from '../../components/host/qr-code-modal'
import { getHuntsInGroup } from '../../utils/host-grouping'
import PersistantCheckbox from '../../components/host/PersistantCheckbox'
import NotificationSendModal from '../../components/host/notificationSendModal'
import NotificationListModal from '../../components/host/notificationListModal'
import TeamTimeRemaining from '../../components/host/team-time-remaining'
import TeamPhotosTab from '../../components/host/team-photos-tab'

export default class HostHuntPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      active_hunt: null,
      group_hunts: [],
      group_photos: {},
      active_image: {},
      modalShow: false,
      teamModalShow: false,
      is_updating: false,
      qrModalShow: false,
      active_team: null,
      notificationSendModalShow: false,
      notificationListModalShow: false,
      photos_updating: false,
      photos_loaded: false,
    }
    this.contextActiveHunts = []
    this.setActivePhoto = this.setActivePhoto.bind(this)
    this.modalClose = this.modalClose.bind(this)
    this.update = this.update.bind(this)
    this.updatePhotos = this.updatePhotos.bind(this)
    this.handleRefresh = this.handleRefresh.bind(this)
    this.teamModalClose = this.teamModalClose.bind(this)
    this.qrModalClose = this.qrModalClose.bind(this)
    this.showTeamQR = this.showTeamQR.bind(this)
    this.notificationSendModalClose = this.notificationSendModalClose.bind(this)
    this.notificationListModalClose = this.notificationListModalClose.bind(this)
    this.getGroupHuntIds = this.getGroupHuntIds.bind(this)
    this.renderTeamBox = this.renderTeamBox.bind(this)
    this.renderMergedTeams = this.renderMergedTeams.bind(this)
  }

  componentDidMount() {
    this.update()
    this.updatePhotos()
    this.interval = setInterval(() => {
      this.update()
      this.updatePhotos()
    }, 3000)
  }

  componentDidUpdate(prevProps, prevState) {
    const huntId = this.props.location.hash.substring(1)
    const prevHuntId = prevProps.location.hash.substring(1)
    if (huntId !== prevHuntId) {
      this.setState({
        teamModalShow: false,
        group_hunts: [],
        group_photos: {},
        photos_loaded: false,
      })
    }
    if (!this.state.active_hunt && prevState.active_hunt) {
      this.setState({
        teamModalShow: false,
        notificationSendModalShow: false,
        notificationListModalShow: false,
      })
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval)
    if (this.setCombinedTeams) {
      this.setCombinedTeams([])
    }
  }

  getGroupHuntIds(hunt_id) {
    const active_hunts = this.contextActiveHunts || []
    const contextHunt = active_hunts.find(h => String(h.id) === String(hunt_id))
    if (contextHunt) {
      const group = getHuntsInGroup(contextHunt, active_hunts)
      if (group.length >= 2) {
        return group
          .slice()
          .sort((a, b) => (a.title || ``).localeCompare(b.title || ``) || a.id - b.id)
          .map(h => h.id)
      }
    }
    return [hunt_id]
  }

  update() {
    const hunt_id = this.props.location.hash.substring(1)
    const { is_updating } = this.state
    if (!hunt_id || is_updating) return
    this.setState({ is_updating: true })
    const ids = this.getGroupHuntIds(hunt_id)
    Promise.all(
      ids.map(id =>
        API.get(`/host/hunt/${id}/`, {
          headers: {
            Authorization: `JWT ${localStorage.getItem(`token`)}`,
          },
        })
          .then(res => res.data)
          .catch(() => null)
      )
    )
      .then(results => {
        const group_hunts = results.filter(Boolean)
        const active_hunt =
          group_hunts.find(h => String(h.id) === String(hunt_id)) || group_hunts[0] || null
        this.setState({ is_updating: false, group_hunts, active_hunt })
        if (active_hunt) {
          this.setActiveHunt(active_hunt)
        }
        if (this.setCombinedTeams) {
          if (group_hunts.length > 1) {
            const combined = []
            group_hunts.forEach(h => {
              const label = h.client || h.display_title || h.title
              ;(h.teams || []).forEach(t => combined.push({ ...t, game_label: label }))
            })
            this.setCombinedTeams(combined)
          } else {
            this.setCombinedTeams([])
          }
        }
      })
      .catch(() => {
        this.setState({ is_updating: false })
      })
  }

  updatePhotos() {
    const hunt_id = this.props.location.hash.substring(1)
    const { photos_updating } = this.state
    if (!hunt_id || photos_updating) return
    this.setState({ photos_updating: true })
    const ids = this.getGroupHuntIds(hunt_id)
    Promise.all(
      ids.map(id =>
        API.get(`hunt/${id}/photos`)
          .then(res => ({ id, photos: res.data.team_photos || [] }))
          .catch(() => ({ id, photos: [] }))
      )
    )
      .then(results => {
        const group_photos = {}
        results.forEach(r => {
          group_photos[r.id] = r.photos
        })
        this.setState({ photos_updating: false, group_photos, photos_loaded: true })
      })
      .catch(() => {
        this.setState({ photos_updating: false, photos_loaded: true })
      })
  }

  handleRefresh() {
    this.update()
    this.updatePhotos()
  }

  getSortedTeams(team_list) {
    if (!team_list || team_list == undefined) return []
    // team_list.map(team => {
    //   team.total_score = this.getTeamScore(team, hunt_duration, timer_enabled)
    // })

    return team_list.sort((a, b) => {
      // check if it's permanent, move it down
      if (a.permanent) return 1
      if (b.permanent) return -1
      // sort by score
      // if the first item has a higher number, move it down
      // if the first item has a lower number, move it up
      if (a.total_score > b.total_score) return -1
      if (a.total_score < b.total_score) return 1
      // tiebreaker (e.g. before the game starts): team number ascending,
      // which keeps games loosely grouped via their team numbers
      return (a.number || 0) - (b.number || 0)
    })
  }

  getTeamScore(team, hunt_duration, timer_enabled) {
    var total_score = 0

    // normal score
    total_score += team.score

    // checklist points
    if (team.checklist_answers !== null) {
      for (var id in team.checklist_answers) {
        total_score += team.checklist_answers[id].points
      }
    }

    // mystery message points
    if (team.mystery_message_answers !== null) {
      total_score += team.mystery_message_answers.points
    }

    // bonus points
    if (team.host_scores !== null) {
      team.host_scores.map(item => {
        total_score += item.value
      })
    }

    if (timer_enabled) {
      total_score -= getTeamTimeDeduction(team, hunt_duration, timer_enabled)
    }

    //console.log(`Team no: ${team.access_code}: ${total_score}`)

    return total_score
  }

  handleSelectedAccordion(event) {
    const target = event.target
    var els = target.parentElement.parentElement.querySelectorAll(`.host-checklist-card .card-header`)
    for (let i = 0; i < els.length; i++) {
      if (!els[i].isEqualNode(target)) {
        els[i].classList.remove(`open`)
      }
    }
    target.classList.toggle(`open`)
  }

  setActivePhoto(photo) {
    this.setState({
      active_image: photo,
      modalShow: true,
    })
  }

  showTeamQR(team) {
    this.setState({
      active_team: team,
      qrModalShow: true,
    })
  }

  modalClose() {
    this.setState({
      modalShow: false,
    })
  }

  teamModalClose() {
    this.setState({
      teamModalShow: false,
    })
  }

  qrModalClose() {
    this.setState({
      qrModalShow: false,
    })
  }

  notificationSendModalClose() {
    this.setState({
      notificationSendModalShow: false,
    })
  }

  notificationListModalClose() {
    this.setState({
      notificationListModalShow: false,
    })
  }

  renderTeamBox(hunt, team, huntPhotos, addPointsTeam) {
    const { photos_loaded } = this.state
    if (team.permanent) {
      return (
        <div key={team.id} className="col-md-6 mb-3">
          <div className="card team-card">
            <div className="card-header bg-danger">
              <div className="row">
                <div className={`col team-number`}>
                  Team No.{` `}
                  {team.number > 0 && <span className={`title h6 mr-4`}>{team.number}</span>}{` `}
                  <span className={`title h6`}>{team.access_code}</span>
                </div>
                <div className={`col text-right`}>Permanent</div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    let team_captain = `(None)`
    let team_status = `Not Started`
    if (team.leader && team.members !== undefined && team.members[team.leader] !== undefined) {
      if (team.members[team.leader].name) {
        team_captain = team.members[team.leader].name
      } else {
        team_captain = `(No Name)`
      }
    }
    if (team.end_time) {
      team_status = `Finished`
    } else if (team.start_time) {
      team_status = `In Progress`
    }
    const teamPhotos = filterPhotosForTeam(huntPhotos, team.id)
    const photosTabTitle = teamPhotos.length > 0 ? `Photos (${teamPhotos.length})` : `Photos`
    return (
      <div key={team.id} className="col-md-6 mb-3">
        <div className={`card team-card`}>
          <div className={`card-header`}>
            <div className={`row`}>
              <div className={`col team-number`}>
                Team No. <span className={`title h6 mr-4`}>{team.number}</span>{` `}
                <span className={`title h6`}>{team.access_code}</span>
              </div>
              <div className={`col text-right`}>
                <button
                  className={`btn py-0 px-4`}
                  onClick={() => {
                    this.showTeamQR(team)
                  }}
                  title="View Team QR Code">
                  <SvgQRCode />
                </button>
                <button
                  className={`btn px-0 py-0`}
                  onClick={() => addPointsTeam(team.id)}
                  title="Add Bonus Points">
                  <SvgCirclePlus />
                </button>
              </div>
            </div>
          </div>
          <div className={`card-body px-0 py-0`}>
            <Tabs defaultActiveKey={`details-${team.id}`} id={`tabs-${team.id}`} className={`host-tabs`}>
              <Tab eventKey={`details-${team.id}`} title={`Details`}>
                <div className="px-4 py-3">
                  <span className={`d-block team-details`}>
                    <strong>Name</strong> {team.team_name ? team.team_name : `(None)`}
                  </span>
                  <span className={`d-block team-details`}>
                    <strong>Captain</strong> {team_captain}
                  </span>
                  <span className={`d-block team-details`}>
                    <strong>Status</strong> {team_status}
                  </span>
                  <TeamTimeRemaining team={team} duration={hunt.duration} enableTimer={hunt.enable_timer} />
                  <span className={`d-block team-details`}>
                    <strong>Starting Question</strong> {team.start} ({team.direction})
                  </span>
                  <span className={`d-block team-details`}>
                    <strong>Player Count</strong> {team.player_count}
                  </span>
                  <span className={`d-block team-details`}>
                    <strong>Notes</strong> {team.notes}
                  </span>

                  <hr />

                  <PersistantCheckbox id={team.id} content="QR Code Shown" />

                  <BonusPointsList
                    team_id={team.id}
                    host_scores={team.host_scores}
                    handle_refresh={this.handleRefresh}
                  />
                </div>
              </Tab>
              {hunt.checklists.length > 0 && (
                <Tab
                  eventKey={`checklists-${team.id}`}
                  title={hunt.checklists.length > 1 ? `Checklists` : `Checklist`}>
                  <div className="px-4 py-3">
                    <Accordion>
                      {hunt.checklists.map(checklist => (
                        <Accordion.Item
                          key={`checklist-${checklist.id}-${team.id}`}
                          eventKey={`accordion-${checklist.id}-${team.id}`}
                          className="host-checklist-card">
                          <Accordion.Header onClick={this.handleSelectedAccordion}>
                            {checklist.display_title}
                          </Accordion.Header>
                          <Accordion.Body className="px-0 py-0">
                              {checklist.questions.map(question => (
                                <div key={`checklist-question-${question.id}-${team.id}`}>
                                  {checklist.type == `photo` ? (
                                    <HostPhotoChecklistQuestion
                                      question={question}
                                      checked={
                                        team.checklist_answers !== null &&
                                        team.checklist_answers[checklist.id] !== undefined &&
                                        team.checklist_answers[checklist.id].checked[question.id] !== undefined &&
                                        team.checklist_answers[checklist.id].checked[question.id].checked
                                      }
                                      photo={
                                        team.checklist_answers !== null &&
                                        team.checklist_answers[checklist.id] !== undefined &&
                                        team.checklist_answers[checklist.id].checked[question.id] !== undefined &&
                                        team.checklist_answers[checklist.id].checked[question.id].photo
                                      }
                                      onSelect={this.setActivePhoto}
                                    />
                                  ) : (
                                    <HostChecklistQuestion
                                      question={question}
                                      checked={
                                        team.checklist_answers !== null &&
                                        team.checklist_answers[checklist.id] !== undefined &&
                                        team.checklist_answers[checklist.id].checked[question.id] !== undefined &&
                                        team.checklist_answers[checklist.id].checked[question.id].checked
                                      }
                                    />
                                  )}
                                </div>
                              ))}
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </div>
                </Tab>
              )}
              <Tab eventKey={`photos-${team.id}`} title={photosTabTitle}>
                <TeamPhotosTab
                  photos={teamPhotos}
                  loading={!photos_loaded}
                  onSelectPhoto={this.setActivePhoto}
                />
              </Tab>
            </Tabs>
          </div>
          <TeamScore
            team_id={team.id}
            team={team}
            host_scores={team.host_scores}
            hunt_duration={hunt.duration}
            enable_timer={hunt.enable_timer}
            handle_refresh={this.handleRefresh}
          />
        </div>
      </div>
    )
  }

  renderMergedTeams(sectionHunts, addPointsTeam) {
    // Merge every team from every game in the group into a single leaderboard,
    // sorted by score (regardless of which game the team came from). Each team
    // stays paired with its own hunt so per-game data (photos, timer, checklists)
    // still resolves correctly.
    const pairs = []
    sectionHunts.forEach(hunt => {
      if (!hunt || hunt.teams === undefined) return
      hunt.teams.forEach(team => pairs.push({ hunt, team }))
    })
    pairs.sort((a, b) => {
      if (a.team.permanent) return 1
      if (b.team.permanent) return -1
      if ((a.team.total_score || 0) > (b.team.total_score || 0)) return -1
      if ((a.team.total_score || 0) < (b.team.total_score || 0)) return 1
      return (a.team.number || 0) - (b.team.number || 0)
    })
    return (
      <div className="row">
        {pairs.map(({ hunt, team }) => {
          const huntPhotos = (this.state.group_photos && this.state.group_photos[hunt.id]) || []
          return this.renderTeamBox(hunt, team, huntPhotos, addPointsTeam)
        })}
      </div>
    )
  }

  render() {
    const {
      active_hunt,
      active_team,
      teamModalShow,
      qrModalShow,
      notificationSendModalShow,
      notificationListModalShow,
      group_hunts,
    } = this.state
    const sectionHunts =
      group_hunts && group_hunts.length > 0 ? group_hunts : active_hunt ? [active_hunt] : []
    return (
      <HostLoginWrapper>
        <HostContext.Consumer>
          {({ addPointsTeam, setActiveHunt, setCombinedTeams, hunts, get_active_hunts }) => {
            this.setActiveHunt = setActiveHunt
            this.setCombinedTeams = setCombinedTeams
            this.contextActiveHunts = get_active_hunts ? get_active_hunts() : hunts || []
            return (
              <>
                {!active_hunt && <h2 className="py-3 px-5">Loading...</h2>}
                {active_hunt && (
                  <>
                    <div className="site-sub-header px-4 py-4 mb-4">
                      <h1 className={`title h5 text-uppercase mb-0`}>
                        {active_hunt.display_title ? active_hunt.display_title : active_hunt.title}
                      </h1>
                      <div className="row">
                        <div className="col">
                          <p className="mb-0">
                            {active_hunt.event_date
                              ? dayjs(active_hunt.event_date).format(`MM/DD/YYYY h:mm A`)
                              : dayjs(active_hunt.start).format(`MM/DD/YYYY h:mm A`)}{` `}
                            <span> - </span>
                            {active_hunt.end && dayjs(active_hunt.end).format(`MM/DD/YYYY h:mm A`)}
                          </p>
                          <p className="mb-0">{active_hunt.location}</p>
                          <p className="mb-0">{active_hunt.client}</p>
                          <p>
                            Total Players Reported: <strong>{active_hunt.player_count}</strong>
                          </p>
                        </div>
                      </div>
                      {active_hunt.hunt_scripts.length > 0 || active_hunt.shared_scripts.length > 0 ? (
                        <Link to={`/host/scripts/#${active_hunt.id}`} className="btn btn-block btn-primary mt-3">
                          View Host Scripts
                        </Link>
                      ) : null}
                    </div>
                    <div className="content px-4 pb-5">
                      <div className="row">
                        <div className="col-md pb-3">
                          <button
                            className="btn btn-block btn-danger"
                            onClick={() => this.setState({ teamModalShow: true })}>
                            See Team Progress
                          </button>
                          <button
                            className="btn btn-block btn-danger"
                            onClick={() => this.setState({ notificationSendModalShow: true })}>
                            Send Message to Teams
                          </button>
                          <button
                            className="btn btn-block btn-danger"
                            onClick={() => this.setState({ notificationListModalShow: true })}>
                            View previous messages
                          </button>
                        </div>
                        <div className="col-md pb-3">
                          <a
                            className="btn btn-block btn-success"
                            href={`/host/gallery/#${active_hunt.id}`}
                            target="_blank"
                            rel="noreferrer">
                            Photo Gallery
                          </a>
                        </div>
                      </div>
                      {this.renderMergedTeams(sectionHunts, addPointsTeam)}
                    </div>
                    <TeamProgressModal
                      hunts={sectionHunts}
                      modalShow={teamModalShow}
                      modalClose={this.teamModalClose}
                    />
                    <NotificationSendModal
                      hunt={active_hunt}
                      modalShow={notificationSendModalShow}
                      modalClose={this.notificationSendModalClose}
                    />
                    <NotificationListModal
                      hunt={active_hunt}
                      modalShow={notificationListModalShow}
                      modalClose={this.notificationListModalClose}
                    />
                  </>
                )}
                <Modal
                  show={this.state.modalShow}
                  onHide={this.modalClose}
                  centered
                  className=""
                  aria-labelledby="photo-details-modal-title">
                  <h3 id="photo-details-modal-title" className="sr-only">
                    Photo Details
                  </h3>
                  <Modal.Body className={`text-center text-uppercase px-0 py-0`}>
                    {this.state.active_image.image !== undefined && (
                      <>
                        <img
                          className="img-fluid"
                          src={getMediaUrl(this.state.active_image.image)}
                          alt={this.state.active_image.description || ``}
                        />
                      </>
                    )}
                    <button className={`btn btn-block btn-tiny text-uppercase mt-5`} onClick={this.modalClose}>
                      Close
                    </button>
                  </Modal.Body>
                </Modal>
                <QRCodeModal team={active_team} modalShow={qrModalShow} modalClose={this.qrModalClose} />
              </>
            )
          }}
        </HostContext.Consumer>
      </HostLoginWrapper>
    )
  }
}
